import { QueryCache, QueryClient } from '@tanstack/react-query';

const transientNetworkErrorMessages = new Set([
	'Network Error',
	'Failed to fetch',
	'Load failed',
	'NetworkError when attempting to fetch resource.',
]);

type QueryError = {
	message?: unknown;
	name?: unknown;
	response?: {
		status?: unknown;
	};
	stack?: unknown;
	status?: unknown;
};

const isQueryError = (error: unknown): error is QueryError => typeof error === 'object' && error !== null;

const getErrorName = (error: unknown) => {
	if (!isQueryError(error) || typeof error.name !== 'string') return undefined;
	return error.name;
};

const getErrorMessage = (error: unknown) => {
	if (!isQueryError(error) || typeof error.message !== 'string') return undefined;
	return error.message;
};

const getErrorStack = (error: unknown) => {
	if (!isQueryError(error) || typeof error.stack !== 'string') return undefined;
	return error.stack;
};

const getHttpStatus = (error: unknown) => {
	if (!isQueryError(error)) return undefined;

	if (typeof error.response?.status === 'number') return error.response.status;
	if (typeof error.status === 'number') return error.status;

	return undefined;
};

const isTransientNetworkError = (error: unknown) => {
	const message = getErrorMessage(error);
	if (!message || !transientNetworkErrorMessages.has(message)) return false;

	const name = getErrorName(error);
	return name === undefined || name === 'TypeError' || message === 'Network Error';
};

export const createQueryClient = () => {
	return new QueryClient({
		queryCache: new QueryCache({
			onError: (error: unknown) => {
				// ZodError indicates API contract mismatch - always log
				if (getErrorName(error) === 'ZodError') {
					console.error('Zod Validation Error Details:', error);
					return;
				}

				// Skip non-actionable errors that flood Datadog with noise
				const is401 = getHttpStatus(error) === 401;

				if (isTransientNetworkError(error) || is401) {
					// These are expected/transient - don't alert on them
					return;
				}

				// Log everything else (5xx, unexpected 4xx, etc.)
				console.error('React Query Error:', {
					message: getErrorMessage(error),
					stack: getErrorStack(error),
					name: getErrorName(error),
				});
			},
		}),
		defaultOptions: {
			queries: {
				// This needs to be higher than the persistence maxAge otherwise it will just remove the
				// stored data as soon as it's loaded
				cacheTime: 48 * 60 * 60 * 1000,
				keepPreviousData: true,
				retry: false,
				staleTime: 10 * 60 * 1000,
			},
		},
	});
};
